import { Box, Skeleton, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import { GetCountries, GetState } from 'react-country-state-city';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { GET_CUSTOMER_DETAILS } from '../../graphql/queries';
import { client } from '../../service/apolloClient';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { MainWrapper } from '../common-styles';
import { EditWrapper } from './manage-customer-styles';
import { openToast } from '../../store/reducers/toastSlice';

const UserDetails = ({ userData }: any) => {
    const { t } = useTranslation();
    const activeHTTPRequests = useSelector((state: RootState) => state?.app?.activeHTTPRequests);
    const dispatch = useDispatch();
    const [customerDetails, setCustomerDetails] = useState<any>();
    const [countriesList, setCountriesList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [selectedCountryId, setCountryId] = useState(0);
    const [selectedStateId, setStateId] = useState(0);
    const [userCountryCode, setUserCountryCode] = useState('');
    const [userStateCode, setUserStateCode] = useState('');

    useEffect(() => {
        GetCountries()?.then((result: any) => {
            const filteredCountries: any = [];
            result.forEach((country: any) => {
                if (country.name === 'India' || country.name === 'United States') {
                    filteredCountries.push({
                        ...country,
                        phone_code: `+${country.phone_code}`,
                    });
                }
                setCountriesList(filteredCountries);
            });
        });
    }, []);
    useEffect(() => {
        const data: any = countriesList.filter((i: any) => i.iso2 === userCountryCode);
        setCountryId(data[0]?.id);
        setStateList([]);
        setStateId(0);
        GetState(data[0]?.id)?.then((result: any) => {
            setStateList(result);
        });
    }, [userCountryCode, countriesList]);
    useEffect(() => {
        const data: any = stateList.filter((i: any) => i.state_code === userStateCode);
        setStateId(data[0]?.id);
    }, [stateList, userStateCode]);
    const getCustomerDetails = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_CUSTOMER_DETAILS,
                variables: { getCustomerDetailsId: userData?.id },
                fetchPolicy: 'network-only',
            });

            if (data?.getCustomerDetails) {
                const address = data?.getCustomerDetails.user_addresses?.[0] || {};

                setCustomerDetails(data?.getCustomerDetails);
                setUserCountryCode(address.country);
                setUserStateCode(address.state);
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
            dispatch(
                openToast({
                    show: true,
                    message: t('unable_load_customer_info_msg'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, userData?.id, t]);
    useEffect(() => {
        getCustomerDetails();
    }, [userData?.id, getCustomerDetails]);

    const getCountryCode = () => {
        const country: any = countriesList.filter((i: any) => i.id === selectedCountryId);
        return country[0]?.name || '--';
    };
    const getStateCode = () => {
        const state: any = stateList.filter((i: any) => i.id === selectedStateId);
        return state[0]?.name || '--';
    };

    if (!customerDetails && activeHTTPRequests > 0)
        return (
            <MainWrapper
                mt={1.5}
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                }}
            >
                <Box
                    mb={2}
                    sx={{
                        height: '50vh', // Full viewport height
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <Skeleton />
                </Box>
            </MainWrapper>
        );
    return (
        <EditWrapper mt={1.5}>
            <Box className="listBox" mb={2}>
                <Box display="flex" alignItems="center" pb={2}>
                    <Typography variant="h5">{t('customer_details')}</Typography>
                </Box>
                <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('first_name')}{' '}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {customerDetails?.first_name || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('last_name')}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {customerDetails?.last_name || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack className="cstVerify cstMobileNumber">
                            <Typography variant="subtitle1" color="info.100">
                                {t('mobile_no')}{' '}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {customerDetails?.phone_number || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Stack className="cstEmail">
                            <Typography variant="subtitle1" color="info.100">
                                {t('email_id')}
                            </Typography>
                            <Typography variant="h5" py={1} sx={{ wordBreak: 'break-all' }}>
                                {customerDetails?.email || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box className="listBox">
                <Box display="flex" alignItems="center" pb={2}>
                    <Typography variant="h5">{t('billing_address')}</Typography>
                </Box>
                <Grid container flexGrow={1} className="details profileDetails" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('country')}{' '}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {getCountryCode()?.toString()}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('address')}{' '}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {customerDetails?.user_addresses?.[0]?.street || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('city')}{' '}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {customerDetails?.user_addresses?.[0]?.city || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('state')}{' '}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {getStateCode()?.toString()}
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack>
                            <Typography variant="subtitle1" color="info.100">
                                {t('zip_code')}
                            </Typography>
                            <Typography variant="h5" py={1}>
                                {customerDetails?.user_addresses?.[0]?.zip_code || '--'}
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </EditWrapper>
    );
};
export default UserDetails;
